import React from 'react';
import PropTypes from 'prop-types';
import { Image as DefaultIcon } from 'react-feather';
import './icon.scss';
import clsx from 'clsx';
import { IconColors, IconSizes } from './icon-dictionary';

const IconWrapper = React.forwardRef(({
  icon, size, color, className, strokeWidth, tabIndex, onClick, disabled,
  testIds, role, ariaLabel, ariaExpanded, ariaHasPopup, onKeyDown,
}, ref) => {
  const classNames = clsx('icon-cont', color, { disabled }, size, className);
  const LibIcon = icon === null ? DefaultIcon : icon;

  const handleOnClick = () => {
    if (disabled) {
      return;
    }
    if (onClick && typeof onClick === 'function') {
      onClick.call(null);
    }
  };

  const handleOnKeyDown = (event) => {
    if (disabled) {
      return;
    }
    if (onKeyDown && typeof onKeyDown === 'function') {
      onKeyDown(event);
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (onClick && typeof onClick === 'function') {
      event.preventDefault();
      onClick.call(null);
      return;
    }
    // Some parents (e.g. the library's ContextMenu/Dropdown) bind a native click listener to
    // this element rather than passing an onClick prop. Keydown on an SVG does not synthesise
    // a click the way it does on a native button, so dispatch one explicitly. Only for
    // elements explicitly marked interactive, so default callers are unaffected.
    if (role === 'button') {
      event.preventDefault();
      event.currentTarget.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    }
  };

  return (
    <LibIcon
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      ref={ref}
      className={classNames}
      strokeWidth={strokeWidth}
      tabIndex={tabIndex}
      role={role}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-disabled={disabled || undefined}
      data-testid={testIds}
    />
  );
});

IconWrapper.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.element,
    PropTypes.elementType, PropTypes.func, PropTypes.oneOf([null])]),
  size: PropTypes.string,
  color: PropTypes.string,
  strokeWidth: PropTypes.number,
  className: PropTypes.string,
  tabIndex: PropTypes.number,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  testIds: PropTypes.string,
  role: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaHasPopup: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onKeyDown: PropTypes.func,
};

IconWrapper.defaultProps = {
  icon: null,
  size: IconSizes.LARGE,
  color: IconColors.GREY,
  strokeWidth: 2,
  className: '',
  // Kept at -1: this component is used app-wide and changing the default would alter tab order
  // on every page. Pass tabIndex={0} at call sites that need to be reachable.
  tabIndex: -1,
  onClick: null,
  disabled: false,
  testIds: '',
  role: undefined,
  ariaLabel: undefined,
  ariaExpanded: undefined,
  ariaHasPopup: undefined,
  onKeyDown: null,
};

export {
  IconWrapper as default,
  IconColors,
  IconSizes,
};
